<template>
  <div class="crud_form_wrapper">
    <div class="form_title_wrapper">
      <h4>{{ $t("BUTTONS.add_notification") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #233076">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <div class="single_step_form_content_wrapper">
      <form @submit.prevent="validateFormInputs">
        <div class="row">
          <base-select-input
            col="6"
            :optionsList="receiverTypes"
            :placeholder="$t('PLACEHOLDERS.receiverType')"
            v-model="data.receiverTypes"
            required
            multiple
          />

          <template v-if="!isAllSelected">
            <base-select-input
              v-for="type in selectedReceiverTypes"
              :key="type.value"
              col="6"
              :optionsList="receiverPools[type.value] || []"
              :placeholder="`${$t('PLACEHOLDERS.recievers')} - ${type.name}`"
              :value="getReceiversByType(type.value)"
              @input="setReceiversByType(type.value, $event)"
              :required="selectedReceiverTypes.length > 0"
              multiple
            />
          </template>

          <div class="col-12">
            <div class="row">
              <base-input
                col="6"
                type="text"
                :placeholder="$t('PLACEHOLDERS.titleAr')"
                v-model.trim="data.titleAr"
                required
              />

              <base-input
                col="6"
                type="text"
                :placeholder="$t('PLACEHOLDERS.titleEn')"
                v-model.trim="data.titleEn"
                required
              />

              <base-input
                col="6"
                rows="7"
                type="textarea"
                :placeholder="$t('PLACEHOLDERS.contentAr')"
                v-model.trim="data.contentAr"
                required
              />

              <base-input
                col="6"
                rows="7"
                type="textarea"
                :placeholder="$t('PLACEHOLDERS.contentEn')"
                v-model.trim="data.contentEn"
                required
              />
            </div>
          </div>

          <div class="btn_wrapper">
            <base-button
              class="mt-2"
              styleType="primary_btn"
              :btnText="$t('BUTTONS.save')"
              :isLoading="isWaitingRequest"
              :disabled="isWaitingRequest"
            />
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: "CreateNotification",

  computed: {
    receiverTypes() {
      return [
        {
          id: 1,
          name: this.$i18n.locale == "ar" ? "المتبرعين" : "Donors",
          value: "clients",
        },
        {
          id: 2,
          name: this.$i18n.locale == "ar" ? "المشرفين" : "Supervisors",
          value: "supervisors",
        },
        {
          id: 3,
          name: this.$i18n.locale == "ar" ? "المناديب" : "Representatives",
          value: "reps",
        },
        {
          id: 4,
          name: this.$t("PLACEHOLDERS.all"),
          value: "all",
        },
      ];
    },

    isAllSelected() {
      return this.data.receiverTypes.some((type) => type.value === "all");
    },

    activeTypeValues() {
      if (this.isAllSelected) {
        return ["clients", "supervisors", "reps"];
      }

      return this.data.receiverTypes
        .map((type) => type.value)
        .filter((value) => value !== "all");
    },

    receiverPools() {
      const addTypeAllOption = (list, type, label) => [
        {
          id: `all_${type}`,
          name: `${this.$t("PLACEHOLDERS.all")} - ${label}`,
          type,
          isAllOption: true,
        },
        ...list,
      ];

      const clients = addTypeAllOption(
        this.data.clients,
        "clients",
        this.$i18n.locale == "ar" ? "المتبرعين" : "Donors"
      );
      const supervisors = addTypeAllOption(
        this.data.supervisors,
        "supervisors",
        this.$i18n.locale == "ar" ? "المشرفين" : "Supervisors"
      );
      const reps = addTypeAllOption(
        this.data.reps,
        "reps",
        this.$i18n.locale == "ar" ? "المناديب" : "Representatives"
      );

      return {
        clients,
        supervisors,
        reps,
      };
    },

    selectedReceiverTypes() {
      return this.data.receiverTypes.filter((type) => type.value !== "all");
    },
  },

  data() {
    return {
      isWaitingRequest: false,

      data: {
        receiverTypes: [],
        clients: [],
        supervisors: [],
        reps: [],
        receivers: [],
        titleAr: null,
        titleEn: null,
        contentAr: null,
        contentEn: null,
      },
    };
  },

  watch: {
    "data.titleAr"(val) {
      if (typeof val === "string" && val.length > 50) {
        this.data.titleAr = val.slice(0, 50);
      }
    },

    "data.titleEn"(val) {
      if (typeof val === "string" && val.length > 50) {
        this.data.titleEn = val.slice(0, 50);
      }
    },

    "data.contentAr"(val) {
      if (typeof val === "string" && val.length > 500) {
        this.data.contentAr = val.slice(0, 500);
      }
    },

    "data.contentEn"(val) {
      if (typeof val === "string" && val.length > 100) {
        this.data.contentEn = val.slice(0, 100);
      }
    },

    "data.receiverTypes"() {
      this.normalizeAllTypeSelection();
      this.removeInvalidReceivers();
    },

    "data.receivers"() {
      this.expandTypeAllSelection();
    },
  },

  methods: {
    getReceiverKey(receiver) {
      return `${receiver.type}_${receiver.id}`;
    },

    areSameReceiverLists(firstList = [], secondList = []) {
      if (firstList.length !== secondList.length) {
        return false;
      }

      const firstKeys = firstList
        .map((receiver) => this.getReceiverKey(receiver))
        .sort();
      const secondKeys = secondList
        .map((receiver) => this.getReceiverKey(receiver))
        .sort();

      return firstKeys.every((key, index) => key === secondKeys[index]);
    },

    normalizeText(value) {
      return (value || "").trim();
    },

    hasMinAndMaxLength(value, min, max) {
      const length = this.normalizeText(value).length;
      return length >= min && length <= max;
    },

    getReceiversByType(type) {
      return this.data.receivers.filter((receiver) => receiver.type === type);
    },

    setReceiversByType(type, selectedReceivers = []) {
      const receiversWithoutType = this.data.receivers.filter(
        (receiver) => receiver.type !== type
      );

      this.data.receivers = [...receiversWithoutType, ...selectedReceivers];
    },

    hasSelectedReceiversForEveryActiveType() {
      return this.activeTypeValues.every(
        (type) => this.getReceiversByType(type).length > 0
      );
    },

    async getClients() {
      try {
        const res = await this.$axios({
          method: "GET",
          url: `clients?page=0&limit=0&is_active=1`,
        });

        const rows = res.data.data?.data || res.data.data || [];
        this.data.clients = rows.map((item) => ({
          id: item.id,
          name: item.name,
          type: "clients",
        }));
      } catch (error) {
        console.log(error?.response?.data?.message);
      }
    },

    async getSupervisors() {
      try {
        const res = await this.$axios({
          method: "GET",
          url: `supervisors?page=0&limit=0&is_active=1`,
        });

        const rows = res.data.data?.data || res.data.data || [];
        this.data.supervisors = rows.map((item) => ({
          id: item.id,
          name: item.name,
          type: "supervisors",
        }));
      } catch (error) {
        console.log(error?.response?.data?.message);
      }
    },

    async getReps() {
      try {
        const res = await this.$axios({
          method: "GET",
          url: `reps?page=0&limit=0&is_active=1`,
        });

        const rows = res.data.data?.data || res.data.data || [];
        this.data.reps = rows.map((item) => ({
          id: item.id,
          name: item.name,
          type: "reps",
        }));
      } catch (error) {
        console.log(error?.response?.data?.message);
      }
    },

    normalizeAllTypeSelection() {
      if (!this.data.receiverTypes?.length) {
        return;
      }

      const hasAll = this.data.receiverTypes.some(
        (type) => type.value === "all"
      );
      if (hasAll) {
        const isAlreadyAllOnly =
          this.data.receiverTypes.length === 1 &&
          this.data.receiverTypes[0]?.value === "all";

        if (!isAlreadyAllOnly) {
          const selectedAllType = this.data.receiverTypes.find(
            (type) => type.value === "all"
          );
          this.data.receiverTypes = selectedAllType ? [selectedAllType] : [];
        }
      }
    },

    removeInvalidReceivers() {
      const allowedTypes = this.activeTypeValues;
      const nextReceivers = this.data.receivers.filter((receiver) =>
        allowedTypes.includes(receiver.type)
      );

      if (!this.areSameReceiverLists(this.data.receivers, nextReceivers)) {
        this.data.receivers = nextReceivers;
      }
    },

    expandTypeAllSelection() {
      if (!this.data.receivers?.length || this.isAllSelected) {
        return;
      }

      const grouped = this.data.receivers.reduce((acc, receiver) => {
        if (!acc[receiver.type]) {
          acc[receiver.type] = [];
        }
        acc[receiver.type].push(receiver);
        return acc;
      }, {});

      const expanded = [];
      this.activeTypeValues.forEach((type) => {
        const selectedForType = grouped[type] || [];
        const hasTypeAll = selectedForType.some(
          (receiver) => receiver.isAllOption
        );
        if (hasTypeAll) {
          expanded.push(...(this.data[type] || []));
        } else {
          expanded.push(
            ...selectedForType.filter((receiver) => !receiver.isAllOption)
          );
        }
      });

      const uniqueMap = new Map();
      expanded.forEach((receiver) => {
        uniqueMap.set(`${receiver.type}_${receiver.id}`, receiver);
      });

      const nextReceivers = [...uniqueMap.values()];
      if (!this.areSameReceiverLists(this.data.receivers, nextReceivers)) {
        this.data.receivers = nextReceivers;
      }
    },

    validateFormInputs() {
      this.isWaitingRequest = true;

      if (!this.data.receiverTypes.length) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.receiverType"));
        return;
      }

      if (
        !this.isAllSelected &&
        (!this.data.receivers.length ||
          !this.hasSelectedReceiversForEveryActiveType())
      ) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.clientAtLeast"));
        return;
      }

      if (!this.hasMinAndMaxLength(this.data.titleAr, 2, 50)) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.titleAr"));
        return;
      }

      if (!this.hasMinAndMaxLength(this.data.titleEn, 2, 50)) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.titleEn"));
        return;
      }

      if (!this.hasMinAndMaxLength(this.data.contentAr, 2, 500)) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.contentAr"));
        return;
      }

      if (!this.hasMinAndMaxLength(this.data.contentEn, 2, 100)) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.contentEn"));
        return;
      }

      this.submitForm();
    },

    async sendNotification({ toType, users = [] }) {
      const requestData = new FormData();
      if (toType) {
        requestData.append("to_type", toType);
      }
      requestData.append("title[ar]", this.normalizeText(this.data.titleAr));
      requestData.append("title[en]", this.normalizeText(this.data.titleEn));
      requestData.append("body[ar]", this.normalizeText(this.data.contentAr));
      requestData.append("body[en]", this.normalizeText(this.data.contentEn));

      users.forEach((userId) => {
        requestData.append("users[]", userId);
      });

      await this.$axios({
        method: "POST",
        url: `notifications`,
        data: requestData,
      });
    },

    async submitForm() {
      try {
        if (this.isAllSelected) {
          await this.sendNotification({ toType: "all" });
        } else {
          const uniqueUserIds = [
            ...new Set(this.data.receivers.map((receiver) => receiver.id)),
          ];

          await this.sendNotification({
            users: uniqueUserIds,
          });
        }

        this.$message.success(this.$t("MESSAGES.sentSuccessfully"));
        this.$router.push({ path: "/all-notifications/all" });
      } catch (error) {
        this.$message.error(error?.response?.data?.message);
      } finally {
        this.isWaitingRequest = false;
      }
    },
  },

  async created() {
    await Promise.all([
      this.getClients(),
      this.getSupervisors(),
      this.getReps(),
    ]);
  },
};
</script>
